/**
 * Open a CMS Scene's RootContent into a TraceViewer and set up its image
 * anchor preview. Does not load ReferenceContent — call `attachCmsContent`
 * for that (editors only; viewer-app skips references intentionally).
 */
import type { TraceViewer } from '../core/TraceViewer';
import type { SceneObject } from '../core/SceneObject';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import {
    alignAnchorToWorld,
    alignPlacementToWorld,
    resolveLegacyHeuristicRotation,
} from '../loaders/ImageMarkerLoader';
import { Logger } from '../utils/Logger';
import { isAbortError, throwIfDisposed } from '../utils/abort';
import { ImageAnchor } from '../behaviors/imageanchor';
import { AnchorOrientation } from '../scenedata/common';
import {
    convertImageAnchorPosition,
    convertImageAnchorRotation,
} from '../loaders/plugins/zipAnchorConversion';
import { Utility } from '../utils/Utility';
import { parseAnchorOrientation, projectPlacementToLegacyAnchor, type PlacementPose } from './localizers';
import { contentDisplayName, contentDownloadUrl, resolveImageAnchorUrl } from './contentUrl';
import {
    DEFAULT_ANCHOR_WIDTH_METERS,
    type CmsContentInput,
    type CmsLoadOptions,
    type CmsSceneInput,
} from './types';

/** Outcome of {@link loadCmsScene} — tells the caller whether the Root row was loaded. */
export interface LoadCmsSceneResult {
    /** True when a `RootContent` row was found and successfully opened. */
    rootLoaded: boolean;
    /**
     * True when the scene has no `RootContent` row at all (newly created scene).
     * Distinct from `rootLoaded === false` due to a load failure: callers can
     * allow the first publish in this case, since there's no Root to overwrite.
     */
    rootMissing: boolean;
    /** True when the scene had an image marker that was previewed and aligned. */
    anchorShown: boolean;
    /**
     * Set when the anchor came from a placement (`scene.anchorPlacement`):
     * the pose the host was (or, with `skipAnchorAlignment`, would be)
     * posed at. For a `legacy_heuristic` placement this is the rotation the
     * content heuristic resolved, and `resolvedFromHeuristic` is true — an
     * editor writes `pose.q` back as the placement's `explicit` rotation.
     */
    anchorPlacement?: {
        placementId: string;
        pose: PlacementPose;
        resolvedFromHeuristic: boolean;
    };
}

/**
 * Bootstrap a CMS Scene into the viewer:
 *   1. Clear any existing scene.
 *   2. Find the RootContent row and `viewer.open(...)` it.
 *   3. For image-marker scenes loaded from legacy `.zip`: rewrite
 *      top-level transforms from Unity AR Foundation anchor frame to
 *      editor (OpenXR-style) anchor frame.
 *   4. Set up the image-anchor preview if the scene has a marker.
 *
 * Failures in step 2 (malformed archive, network error, missing Root row,
 * unsupported format) are logged and swallowed — the scene is cleared and
 * step 4 still runs so the caller's anchor preview comes up. The return
 * value reports what actually succeeded so editor callers can gate
 * Publish/snapshot wiring on a successful Root load. A viewer disposed
 * mid-load is not a failure: its `AbortError` is rethrown, since there is
 * nothing left to bootstrap.
 *
 * ReferenceContent is NOT loaded here — call `attachCmsContent` for that
 * (editor-only; viewer-app skips references).
 */
export async function loadCmsScene(
    viewer: TraceViewer,
    scene: CmsSceneInput,
    contents: CmsContentInput[],
    blobBaseUrl: string,
    options: CmsLoadOptions = {},
): Promise<LoadCmsSceneResult> {
    const result: LoadCmsSceneResult = { rootLoaded: false, rootMissing: false, anchorShown: false };

    const root = findRootContent(contents);
    const rootUrl = root ? contentDownloadUrl(blobBaseUrl, root) : null;
    const needsImageAnchorFrameConversion = shouldApplyImageAnchorFrameConversion(scene, rootUrl);
    const isLegacyZip = isLegacyZipUrl(rootUrl);

    if (root && rootUrl) {
        Logger.log(`[CMS] Opening RootContent: ${contentDisplayName(root)}`);
        try {
            await viewer.open({
                src: rootUrl,
                onProgress: options.onProgress,
                deferRendering: options.deferRendering,
            });
            result.rootLoaded = true;
        } catch (error) {
            // Only a disposed viewer's: one with no scene left. Any other AbortError is a failed load.
            if (isAbortError(error) && !viewer.traceScene) throw error;
            // Root load failed (malformed archive, network error, unsupported
            // format, etc.). Log and continue — the anchor block below still
            // runs, and the caller can gate Publish wiring on
            // `result.rootLoaded`. We `clearScene` so the caller isn't left
            // with stale content under the new anchor preview.
            Logger.error(`[CMS] Failed to open RootContent: ${String(error)}`);
            viewer.clearScene();
        }
    } else if (!root) {
        // No Root row to load — clear so the caller doesn't end up with the
        // previous scene's content under a new anchor preview.
        Logger.warn(`[CMS] Scene has no RootContent row (${contents.length} content entries total)`);
        viewer.clearScene();
        result.rootMissing = true;
    }

    if (scene.imageUrl) {
        const fullImageUrl = resolveImageAnchorUrl(blobBaseUrl, scene.imageUrl);
        const fallback = options.defaultAnchorWidthMeters ?? DEFAULT_ANCHOR_WIDTH_METERS;
        const widthMeters = scene.imageWidth ?? fallback;
        // New scenes (no RootContent row yet) with no CMS-recorded
        // orientation get an explicit `Vertical` default. Legacy scenes
        // with content but no recorded orientation stay on `Free` so the
        // content-detection heuristic in applyAnchorRotation can run.
        const isNewScene = result.rootMissing && (scene.imageOrientation == null);
        const orientation = isNewScene
            ? AnchorOrientation.Vertical
            : parseAnchorOrientation(scene.imageOrientation);
        const pitchDegrees = scene.imagePitchDegrees ?? 0;

        // The scene's root ENTITY (SCENE_ROOT_SPEC §5.1): the document root the
        // file was opened with, which is where a CMS-managed `ImageAnchor`
        // belongs under D8. A legacy flat `.zip` states no root node of its
        // own, and its anchor stays on the engine root as before.
        const sceneRoot = viewer.traceScene?.getSessionDocumentRoot() ?? viewer.traceScene?.getSceneRoot();
        if (sceneRoot) {
            // Legacy `.zip` image-marker scenes store top-level transforms
            // in Unity AR Foundation's anchor-local frame (+Y normal,
            // +Z top, LH). Convert to the editor's OpenXR-style frame
            // (+Z normal, +Y top, RH) before any preview / alignment
            // runs. Skipped for .gltf / .glxf / .tracex / .trace marker scenes —
            // those are OpenXR-native already. `result.rootLoaded` gates
            // this: a failed open leaves no children to convert.
            if (needsImageAnchorFrameConversion && result.rootLoaded) {
                applyImageAnchorFrameConversion(sceneRoot);
            }

            const placement = scene.anchorPlacement;
            if (placement) {
                // Placement path (TraceBackend#999): the pose is `P`. The
                // ImageAnchor still carries the marker for its preview and
                // for consumers of the legacy fields; its orientation/pitch
                // are the placement's legacy projection, so a later
                // re-commit of them reproduces the same rotation. A
                // `legacy_heuristic` row resolves exactly as the legacy path
                // would, from the same orientation.
                const resolvedFromHeuristic = placement.rotationSource === 'legacy_heuristic';
                const pose: PlacementPose = {
                    t: [...placement.t],
                    q: resolvedFromHeuristic
                        ? resolveLegacyHeuristicRotation(sceneRoot, orientation, pitchDegrees)
                        : [...placement.q],
                };
                const projection = projectPlacementToLegacyAnchor(pose);
                await sceneRoot.setBehavior(ImageAnchor, {
                    src: fullImageUrl,
                    widthMeters,
                    ...(projection ?? {}),
                });
                throwIfDisposed(viewer);
                if (!options.skipAnchorAlignment) {
                    alignPlacementToWorld(viewer, pose, widthMeters);
                }
                result.anchorPlacement = { placementId: placement.id, pose, resolvedFromHeuristic };
            } else {
                await sceneRoot.setBehavior(ImageAnchor, {
                    src: fullImageUrl,
                    widthMeters,
                    orientation,
                    pitchDegrees,
                });
                throwIfDisposed(viewer);
                if (!options.skipAnchorAlignment) {
                    alignAnchorToWorld(viewer, widthMeters);
                }
            }
            // XR (#933): the offset reference space maps the mount onto the
            // marker, so the host must sit at identity. A document root can
            // come back holding the editor's desktop preview pose (Rx(pitch)
            // plus a lift) from a save made before the writer stopped
            // recording it; composing that pose would tilt the content twice.
            // After the anchor is set, on purpose: when an older file carried
            // the anchor, the re-commit re-poses the host keeping the
            // children's world pose — what the editor shows relative to the
            // marker — and the reset then drops only the host's own pose.
            // A legacy `.zip` is left as it is: its writer never records the
            // host (the session root is the node the format omits), so its
            // XR path stays exactly as it was.
            if (options.skipAnchorAlignment && !isLegacyZip) {
                resetAnchorHostPose(sceneRoot);
            }
            result.anchorShown = true;
        } else {
            Logger.warn('[CMS] Cannot attach ImageAnchor — no scene root');
        }
    }

    return result;
}

/**
 * Whether `loadCmsScene` should rewrite top-level transforms from the
 * Unity AR Foundation image-anchor frame to the editor anchor frame.
 *
 * True iff:
 *   - the CMS scene has a marker image attached (`scene.imageUrl` set), AND
 *   - the loaded content URL is a legacy `.zip` (extension check, with
 *     query strings stripped).
 *
 * `.gltf` / `.glxf` / `.tracex` / `.trace` image-marker scenes are OpenXR-native
 * end-to-end and need no conversion — AR Foundation conventions are
 * strictly a `.zip` quirk.
 *
 * We can't use the runtime scene-root `ImageAnchor` behavior as the
 * signal here — it isn't attached until later in {@link loadCmsScene}.
 * Contrast with the export side in `TraceExport.prepareExport`, which
 * DOES read the runtime `ImageAnchor` because by export time the
 * behavior is in place.
 *
 * Exported for testing.
 */
export function shouldApplyImageAnchorFrameConversion(
    scene: Pick<CmsSceneInput, 'imageUrl'>,
    rootUrl: string | null,
): boolean {
    return !!scene.imageUrl && isLegacyZipUrl(rootUrl);
}

/** Whether the RootContent is a legacy `.zip` (extension check, query stripped). */
function isLegacyZipUrl(rootUrl: string | null): boolean {
    return !!rootUrl && Utility.getFileExtensionFromSrc(rootUrl) === 'zip';
}

/**
 * Walk top-level children of `sceneRoot` and convert each transform from
 * the Unity AR Foundation image-anchor frame to the editor anchor frame.
 *
 * Operates in-place on the children's local transforms (position and
 * rotationQuaternion). **Not idempotent** — this is a self-inverse
 * (involution): applying it twice returns the original values. Callers
 * must NOT apply this twice to the same scene root.
 *
 * Top-level only: nested children inherit the frame change through
 * their parent's transform automatically.
 *
 * Assumes Babylon stores rotation as `rotationQuaternion`, which the
 * `.zip` scene loader always populates (see
 * `sceneLoadingUtils.applyTransform`). Children with `rotationQuaternion`
 * unset are treated as identity — their position is still converted.
 * Euler `rotation` is not read; a node with Euler-only rotation would
 * have its rotation silently skipped, but the loader never produces
 * those, so the assumption holds in practice.
 *
 * Exported for testing.
 */
export function applyImageAnchorFrameConversion(sceneRoot: SceneObject): void {
    for (const child of sceneRoot.getChildren()) {
        const pos = child.transform.position;
        const converted = convertImageAnchorPosition([pos.x, pos.y, pos.z])!;
        pos.set(converted[0], converted[1], converted[2]);

        const q = child.transform.rotationQuaternion;
        if (q) {
            const cq = convertImageAnchorRotation([q.x, q.y, q.z, q.w])!;
            child.transform.rotationQuaternion = new Quaternion(cq[0], cq[1], cq[2], cq[3]);
        }
    }
}

/** Pick the row that becomes the scene root. Today: must be tagged RootContent. */
export function findRootContent(contents: CmsContentInput[]): CmsContentInput | null {
    return contents.find(c => c.contentType === 'RootContent') ?? null;
}

/**
 * Put the image-anchor host at identity for XR (#933): position, rotation and
 * scale. On the desktop the marker preview hangs below the host, so the host's
 * pose never changes where content sits relative to the marker; in XR the
 * marker is the mount, and any pose left on the host is applied on top of it.
 *
 * Load-time only: it must run before any XR pose lands on this node. With no
 * document root the host is the engine root, and a later caller that poses the
 * host for a session (a localizer placement session poses it at `P`) captures
 * and restores the pose it finds. Calling this mid-session would clobber that
 * pose, so it lives here in the load path and nowhere else.
 */
function resetAnchorHostPose(host: SceneObject): void {
    host.transform.position.set(0, 0, 0);
    host.transform.rotationQuaternion = Quaternion.Identity();
    host.transform.scaling.set(1, 1, 1);
}
